import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  AuthTokenResponse,
  PermissionEnum,
  ShortOrganizationForUser,
  UserInfo,
} from '@shared/api';

export type InitialStateType = {
  authInfo: AuthTokenResponse | null;
  userInfo: UserInfo | null;
  currentOrganizationInfo: ShortOrganizationForUser;
};

const getExp = (jwt?: string | null) => {
  if (!jwt) return 0;
  try {
    const [, payload] = jwt.split('.');
    return JSON.parse(atob(payload)).exp as number;
  } catch {
    return 0;
  }
};

const initialOrganizationInfo: ShortOrganizationForUser = {
  id: 0,
  title: '',
  permission: 'OWNER',
  credit_balance: 0,
};

const initialState: InitialStateType = {
  authInfo: null,
  userInfo: null,
  currentOrganizationInfo: initialOrganizationInfo,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokens: (state, action: PayloadAction<AuthTokenResponse>) => {
      const next = action.payload;
      const nextExp = getExp(next.access_token);
      const prev = state.authInfo;
      const prevExp = getExp(prev?.access_token ?? null);
      const refreshChanged = Boolean(
        next.refresh_token && next.refresh_token !== prev?.refresh_token,
      );

      if (refreshChanged || !prevExp || (nextExp && nextExp >= prevExp)) {
        state.authInfo = next;
      }
    },
    setUserInfo: (state, action: PayloadAction<UserInfo>) => {
      state.userInfo = action.payload;

      const currentOrganizationInfo = state.currentOrganizationInfo;
      const organizations = action.payload.organizations || [];
      const matchedOrganization = organizations.find((c) => c.id === currentOrganizationInfo.id);

      if (matchedOrganization) {
        if (matchedOrganization.title !== currentOrganizationInfo.title) {
          state.currentOrganizationInfo.title = matchedOrganization.title;
        }

        state.currentOrganizationInfo = matchedOrganization;
      } else {
        state.currentOrganizationInfo = organizations[0] ?? initialOrganizationInfo;
      }
    },
    addUserOrganization: (state, action: PayloadAction<ShortOrganizationForUser>) => {
      if (!state.userInfo?.id) return;

      state.userInfo = {
        ...state.userInfo,
        organizations: [...(state.userInfo.organizations || []), action.payload],
      };
    },
    updateUserOrganization: (state, action: PayloadAction<ShortOrganizationForUser>) => {
      if (!state.userInfo?.id) return;

      const organizations = state.userInfo.organizations || [];
      const exists = organizations.some((organization) => organization.id === action.payload.id);

      state.userInfo.organizations = exists
        ? organizations.map((organization) =>
            organization.id === action.payload.id ? { ...action.payload } : organization,
          )
        : [...organizations, action.payload];
    },
    setCurrentOrganizationInfo: (
      state,
      action: PayloadAction<ShortOrganizationForUser | undefined>,
    ) => {
      if (!action.payload) {
        state.currentOrganizationInfo = initialOrganizationInfo;
        return;
      }

      state.currentOrganizationInfo = action.payload;
    },
    setCurrentOrganizationName: (state, action: PayloadAction<string>) => {
      if (!state.currentOrganizationInfo) {
        state.currentOrganizationInfo = { ...initialOrganizationInfo };
      }
      state.currentOrganizationInfo.title = action.payload;
    },
    setSelfPermissions: (state, action: PayloadAction<PermissionEnum>) => {
      if (!state.currentOrganizationInfo) {
        state.currentOrganizationInfo = { ...initialOrganizationInfo };
      }

      state.currentOrganizationInfo.permission = action.payload;
    },
    logout: (state) => {
      const preservedOrganization = state.currentOrganizationInfo;
      return {
        ...initialState,
        currentOrganizationInfo: preservedOrganization.id
          ? preservedOrganization
          : initialOrganizationInfo,
      };
    },
  },
});

export const { reducer: authReducer, actions: authActions } = authSlice;
